import { useSuspenseQuery } from '@apollo/client/react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Info, SearchX, UsersRoundIcon } from 'lucide-react';
import { useState, useTransition } from 'react';
import { debounce } from 'lodash';

import { IntegrationsDocument } from 'src/graphql/actions/integration/integrations.query';
import { withSuspense } from 'src/hoc';
import { PROVIDER_KEY_TO_NAME, INTEGRATIONS_LIMIT } from 'src/shared/constants';
import { renderLogo } from 'src/utils/renderLogo';
import { getProviderDisplayName } from 'src/utils/getProviderDisplayName';
import { Badge } from '@/components/ui/badge';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Paginator from '@/components/ui/paginator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

import type { IIntegrationsQuery } from 'src/graphql/actions/integration/integrations.query';

import IntegrationDropdownMenu from './IntegrationDropdown';
import StatusFilter from './StatusFilter';

type IIntegrationRow = IIntegrationsQuery['integrations']['nodes'][number];

const columnHelper = createColumnHelper<IIntegrationRow>();

const OwnerTooltip = () => {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger>
          <Info className="size-4" />
        </TooltipTrigger>
        <TooltipContent side="bottom" align="center" className="max-w-55">
          The owner is the user whose credentials authenticate this integration. They manage access and the connection.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const UserAccessTooltip = () => {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger>
          <Info className="size-4" />
        </TooltipTrigger>
        <TooltipContent side="bottom" align="center" className="max-w-55">
          Users with access can use this integration to add assets to their products.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const columns = [
  columnHelper.display({
    id: 'integration',
    header: 'Integration',
    cell: ({ row }) => {
      const { provider, accountName } = row.original;
      const Icon = renderLogo(provider as keyof typeof PROVIDER_KEY_TO_NAME);
      return (
        <div className="flex items-center gap-3">
          <Icon className="size-5 shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">{accountName}</span>
            <span className="text-xs text-muted-foreground">{getProviderDisplayName(provider)}</span>
          </div>
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'owner',
    header: () => (
      <div className="flex items-center gap-1">
        Owner <OwnerTooltip />
      </div>
    ),
    cell: ({ row }) =>
      row.original.owner?.name ?? (
        <Badge variant="outline" className="font-medium whitespace-nowrap">
          Deleted user
        </Badge>
      ),
  }),
  columnHelper.accessor('membersCount', {
    id: 'usersWithAccess',
    header: () => (
      <div className="flex items-center gap-1">
        Users with access <UserAccessTooltip />
      </div>
    ),
    cell: (props) => (
      <span className="flex items-center gap-0.5">
        <UsersRoundIcon className="size-3.5 text-gray-500" /> {props.getValue()}
      </span>
    ),
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.active ? (
        <Badge className="bg-green-100 text-green-800">Active</Badge>
      ) : (
        <Badge className="bg-slate-100 text-slate-800">Disconnected</Badge>
      ),
  }),
  columnHelper.display({
    id: 'action',
    header: '',
    cell: ({ row }) => {
      const { id, provider, policy, __typename } = row.original;
      return (
        <IntegrationDropdownMenu
          id={id}
          provider={provider}
          __typename={__typename}
          canManage={policy.update}
          canRead={policy.read}
        />
      );
    },
    meta: { cellClassName: 'text-right' },
  }),
];

const IntegrationTable = () => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>();
  const [page, setPage] = useState(1);
  const [isPending, startTransition] = useTransition();

  const {
    data: { integrations },
  } = useSuspenseQuery(IntegrationsDocument, {
    variables: {
      page,
      perPage: INTEGRATIONS_LIMIT,
      ...(query && { query }),
      ...(typeof statusFilter === 'boolean' && { active: statusFilter }),
    },
  });

  const debouncedOnSearch = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      startTransition(() => {
        setPage(1);
        setQuery(e.target.value);
      }),
    500,
  );

  const onClear = () => {
    setPage(1);
    setQuery('');
  };

  const onPageChange = (p: number) => startTransition(() => setPage(p));

  const onStatusChange = (value: boolean | undefined) => startTransition(() => setStatusFilter(value));

  const table = useReactTable({
    data: integrations.nodes,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <StatusFilter onChange={onStatusChange} value={statusFilter} />
        <InputSearch
          placeholder="Search integrations"
          onChange={debouncedOnSearch}
          isLoading={isPending}
          className="w-full sm:w-75 [&>input]:bg-white"
          onClear={onClear}
        />
      </div>
      <Card className="shadow-none">
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md h-11"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {integrations.nodesCount ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length}>
                  <EmptyBlock
                    title="No results found"
                    description="There were no results for your search. Try another filter."
                    icon={SearchX}
                    withCard={false}
                    classes={{
                      title: 'text-base',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Paginator
        currentPage={page}
        totalPages={integrations.pagesCount}
        onPageChange={onPageChange}
        showPreviousNext
        hideOnSinglePage
      />
    </div>
  );
};

export default withSuspense(IntegrationTable);
